<template>
    <hts-map
        v-if="initialized"
        ref="map"
        :minus-height="minusHeight"
        :center-position="centerPosition"
        :draggable="draggable"
        @update-directions="updateDirections">
        <template #top>
            <slot name="top" />
        </template>
        <template #bottom>
            <slot name="bottom" />
        </template>
    </hts-map>
</template>

<script lang="ts" setup>
const { library } = $store()
const props = defineProps({
  mapStyle: {
    type: String,
    default: ''
  },
  centerPosition: Object as PropType<any>,
  centerMarker: Boolean,
  routeSeparated: {
    type: Object,
    default: null
  },
  route: {
    type: Object,
    default: null
  },
  latLngLocationRoutes: {
    type: Array
  },
  showLocation: {
    type: Boolean
  },
  minusHeight: {
    type: Number,
    default: 0
  },
  draggable: {
    type: Boolean,
    default: false
  },
  vehicle: {
    type: Object as PropType<any>,
    default: () => {
      return null
    }
  },
  startLocation: {
    type: Object as PropType<any>,
    default: () => {
      return null
    }
  },
  stopLocation: {
    type: Object as PropType<any>,
    default: () => {
      return null
    }
  },
  expectedPaths: {
    type: Array,
    default: () => []
  },
  trackingPaths: {
    type: Array,
    default: () => []
  },
  last24hPaths: {
    type: Array,
    default: () => []
  },
  directionColor: {
    type: String,
    default: '#03b4f2'
  }
})
const initialized = ref(true)
const emits = defineEmits(['click', 'clickMarker', 'updateDirections'])

const {
  route,
  routeSeparated,
  showLocation,
  latLngLocationRoutes,
  draggable,
  vehicle,
  startLocation,
  stopLocation,
  expectedPaths,
  trackingPaths,
  last24hPaths,
  directionColor
} = toRefs(props)
const map: any = ref(null)
const vehicleLocation = computed(() => vehicle?.value?.gpsInfo)
// toàn bộ diem cua he thong
const nodes = computed(() => {
  return library.locations
})
const ready = async () => {
  let interval: NodeJS.Timeout | undefined
  return await new Promise((resolve) => {
    interval = setInterval(() => {
      if (map.value?.isReady) {
        clearInterval(interval)
        resolve(true)
      }
    }, 100)
  })
}

onMounted(() => {
  map.value?.ready().then(() => {
    removeMarkers()
    createMarkers()

    // marker custom
    directionCreateMarkers()

    // route
    renderRouteSeparated()

    // marker của route
    renderDirectionMarkers()
  })
})

watch(
  () => route.value,
  () => {
    renderDirectionMarkers()
  },
  { deep: true }
)

watch(
  () => routeSeparated.value,
  (val) => {
    renderRouteSeparated()
  },
  { deep: true }
)

watch(
  () => draggable.value,
  (val) => {
    renderRouteSeparated()
  },
  { deep: true }
)

watch(
  () => latLngLocationRoutes?.value,
  (val: any) => {
    directionCreateMarkers()
  },
  { deep: true }
)

watch(
  () => showLocation.value,
  (val: any) => {
    directionCreateMarkers()
  },
  { deep: true }
)

const reDrawRoute = () => {
  renderRouteSeparated()
}

const directionCreateMarkers = $lodash.debounce((latLngs?: google.maps.LatLngLiteral[]) => {
  removeMarkers()
  createMarkers((latLngs as { [key: string]: any }[]) || latLngLocationRoutes?.value || undefined)
}, 100)

const createMarkers = (latLngs?: { [key: string]: any }[]) => {
  if (!map.value) {
    return
  }

  map.value.ready().then(() => {
    nodes.value.forEach(async (node: any) => {
      const lat = parseFloat(node.lat)
      const lng = parseFloat(node.lng)
      const id = node._id

      let draw = false
      if (showLocation.value) {
        draw = true
      }

      if (latLngs && Array.isArray(latLngs) && latLngs.length) {
        for (let i = 0; i < latLngs.length; i++) {
          const sLat = parseFloat(latLngs[i]?.lat?.toString())
          const sLng = parseFloat(latLngs[i]?.lng?.toString())
          const sId = latLngs[i]?.nodeId

          if (lat === sLat && sLng === lng) {
            draw = true
          }

          if (sId && id === sId) {
            draw = true
          }
        }
      }

      if (!draw) {
        return
      }

      await createMarker(node)
    })
  })
}

const createMarker = async (node: any) => {
  const lat = parseFloat(node.lat)
  const lng = parseFloat(node.lng)
  const innerHtml = node.newLocation
    ? `
<div><?xml version="1.0" encoding="iso-8859-1"?>
<!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
<svg fill="currentColor" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
viewBox="0 0 34.398 34.398" xml:space="preserve">
<g>
<g id="c28_geolocalization">
<path d="M17.202,0C10.749,0,5.515,5.197,5.515,11.607c0,3.281,3.218,9.156,3.218,9.156l8.039,13.635l8.386-13.477
  c0,0,3.726-5.605,3.726-9.314C28.883,5.197,23.653,0,17.202,0z M17.147,18.002c-3.695,0-6.688-2.994-6.688-6.693
  c0-3.693,2.993-6.686,6.688-6.686c3.693,0,6.69,2.992,6.69,6.686C23.837,15.008,20.84,18.002,17.147,18.002z"/>
<polygon points="18.539,7.233 15.898,7.233 15.898,10.242 12.823,10.242 12.823,12.887 15.898,12.887 15.898,15.985
  18.539,15.985 18.539,12.887 21.576,12.887 21.576,10.242 18.539,10.242 		"/>
</g>
<g id="Capa_1_146_">
</g>
</g>
</svg></div>`
    : `<div><svg  xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 256 256" xml:space="preserve">
<defs>
</defs>
<g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
<path d="M 45 0 C 27.395 0 13.123 14.272 13.123 31.877 c 0 7.86 2.858 15.043 7.573 20.6 L 45 81.101 l 24.304 -28.624 c 4.716 -5.558 7.573 -12.741 7.573 -20.6 C 76.877 14.272 62.605 0 45 0 z M 45 43.889 c -7.24 0 -13.11 -5.869 -13.11 -13.11 c 0 -7.24 5.869 -13.11 13.11 -13.11 s 13.11 5.869 13.11 13.11 C 58.11 38.02 52.24 43.889 45 43.889 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
<path d="M 58.958 71.559 L 45 82.839 L 31.057 71.556 c -9.329 1.65 -15.682 4.901 -15.682 8.645 c 0 5.412 13.263 9.8 29.625 9.8 c 16.361 0 29.625 -4.388 29.625 -9.8 C 74.625 76.458 68.278 73.209 58.958 71.559 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
</g>
</svg></div>`
  const htmlContent = document.createElement('div')
  htmlContent.className = 'hts-marker text-blue-500' + (node.className || '')
  const text = document.createElement('span')
  const icon = document.createElement('img')
  htmlContent.innerHTML = innerHtml
  text.textContent = node.locationName || $t('labels.unknown')
  htmlContent.appendChild(icon)
  htmlContent.appendChild(text)
  const marker = await map.value.createAdvanceMarker(
    {
      lat,
      lng
    } as google.maps.LatLng | google.maps.LatLngLiteral,
    htmlContent
  )
  marker.addListener('click', () => {
    emits('clickMarker', node)
    map.value.hideInfoWindow()
    map.value.hideClickPositionMarker()
  })
}

const removeMarkers = () => {
  map.value && map.value.removeAdvanceMarkers()
}

// vẽ tuyến & polyline
const renderRouteSeparated = $lodash.debounce(() => {
  if (!routeSeparated.value) {
    return
  }

  if (!map.value) {
    return
  }

  const { routes, options } = routeSeparated?.value || ({} as any)

  const bounds = new google.maps.LatLngBounds()

  const points = [] as any[]

  map.value?.ready().then(() => {
    map.value?.clearDirections()
    map.value?.clearPolyline()

    routes?.forEach((item: any) => {
      const { originData, destinationData, data } = item

      points.push(originData)
      points.push(destinationData)

      const overViewPolyLine = data?.overViewPolyLine || null

      if (!overViewPolyLine || draggable.value) {
        map.value.drawDirection(originData, destinationData, 'DRIVING', [], {
          multiple: !!options?.multiple,
          draggable: !!draggable.value,
          data,
          polylineStyle: {
            zIndex: 100,
            strokeColor: directionColor.value,
            strokeOpacity: 1.0,
            strokeWeight: 12
          }
        })
      } else {
        const decodedPath = google.maps.geometry.encoding.decodePath(overViewPolyLine)
        map.value.drawPolyline(decodedPath, {
          polylineStyle: {
            zIndex: 100,
            strokeColor: directionColor.value,
            strokeOpacity: 1.0,
            strokeWeight: 12
          }
        })
      }
    })
  })

  points.forEach((point) => {
    // Thêm từng điểm vào bounds
    bounds.extend(new google.maps.LatLng(point.lat, point.lng))
  })

  map.value.fitBounds(bounds)
}, 100)

// vẻ markers trong tuyến
const renderDirectionMarkers = $lodash.debounce(() => {
  const { originData, destinationData, waypointsData, options } = route.value

  map.value?.ready().then(() => {
    map.value.generateDirectionMarkers(
      {
        location: originData,
        name: options?.originLocationName
      },
      {
        location: destinationData,
        name: options?.destinationLocationName
      },
      waypointsData,
      {
        sameOriginDestination: !!options?.sameOriginDestination,
        renderMarkerByIndexNumber: !!options?.renderMarkerByIndexNumber
      }
    )
  })
}, 100)

const resetMap = () => {
  map.value?.ready().then(() => {
    map.value?.resetMap()
  })
}

const clearDirections = () => {
  map.value?.ready().then(() => {
    map.value?.clearDirections()
  })
}

const updateDirections = (data: any) => {
  emits('updateDirections', data)
}

// Render tracking paths
watch(
  () => vehicleLocation.value,
  (val: any) => {
    autoRenderExpectedPaths()
    autoRenderTrackingPaths()
  },
  { deep: true }
)
watch(
  () => startLocation.value,
  (val: any) => {
    autoRenderExpectedPaths()
    autoRenderTrackingPaths()
  },
  { deep: true }
)
watch(
  () => stopLocation.value,
  (val: any) => {
    autoRenderExpectedPaths()
    autoRenderTrackingPaths()
  },
  { deep: true }
)
const lineSymbol = {
  path: 'M 0,-1 0,1',
  strokeOpacity: 1,
  strokeColor: '#03b4f2',
  strokeWeight: 8,
  scale: 4
}
const autoRenderExpectedPaths = () => {
  renderExpectedPaths(stopLocation.value, vehicleLocation.value, vehicle.value)
}
const renderExpectedPaths = (stopLoc: any, vehicleLoc: any, vehicle: any) => {
  if (vehicle) {
    map.value?.ready().then(async () => {
      map.value.removeAllMarkerList('journey-vehicle')
      await drawVehicle(vehicle)
    })
  }
  if (!vehicleLoc || !stopLoc) {
    return
  }

  const from = {
    lat: parseFloat(vehicleLoc.lat.toString()),
    lng: parseFloat(vehicleLoc.lng.toString())
  }
  const to = {
    lat: parseFloat(stopLoc.lat.toString()),
    lng: parseFloat(stopLoc.lng.toString())
  }
  map.value?.ready().then(() => {
    map.value.drawCustomDirection(from, to, {
      polylineStyle: {
        zIndex: 200,
        strokeOpacity: 0,
        icons: [
          {
            icon: lineSymbol,
            offset: '0',
            repeat: '20px'
          }
        ]
      }
    })
    drawVehicle(vehicle)
  })
}

const drawVehicle = async (vehicle: any) => {
  let content = `<div><p><strong>${vehicle.licensePlate}</strong></p>`
  content += '</div>'
  const infoWindow = new google.maps.InfoWindow({
    content,
    pixelOffset: new google.maps.Size(0, -35)
  })

  const htmlContent = document.createElement('div')
  // htmlContent.innerHTML = `<svg fill="#000000" height="80px" width="80px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
  // 	 viewBox="0 0 330 330" xml:space="preserve">
  // <path id="XMLID_520_" d="M165,0C74.019,0,0,74.019,0,165s74.019,165,165,165s165-74.019,165-165S255.982,0,165,0z M165,300
  // 	C90.561,300,30,239.44,30,165S90.561,30,165,30c74.439,0,135,60.561,135,135S239.439,300,165,300z"/>
  // </svg>`
  htmlContent.innerHTML = `
<div style="top:10px;position:relative;background:#FFF;border-radius:50%;border:4px solid #03b4f2;width:34px;height:34px"></div>`
  if (vehicle?.gpsInfo) {
    const { lat, lng } = vehicle?.gpsInfo
    const marker = await map.value.createAdvanceMarker({ lat, lng }, htmlContent, vehicle, { list: 'journey-vehicle', styles: { zIndex: 999 } })
    marker?.addListener('click', () => {
      infoWindow.close()
      infoWindow.open(map, marker)
    })

    setTimeout(() => {
      map.value.panTo({ lat, lng })
    }, 1000)
  }
}
const autoRenderTrackingPaths = () => {
  renderTrackingPaths(startLocation.value, vehicleLocation.value, trackingPaths?.value)
}

const renderTrackingPaths = (startLoc: any, vehicleLoc: any, paths: any[]) => {
  map.value?.ready().then(() => {
    if (paths.length) {
      map.value.drawPolyline(paths, {
        noSave: true,
        polylineStyle: {
          zIndex: 202,
          strokeColor: '#03b4f2',
          strokeOpacity: 1.0,
          strokeWeight: 12
        }
      })
    } else if (startLoc && vehicleLoc) {
      const from = {
        lat: parseFloat(startLoc.lat.toString()),
        lng: parseFloat(startLoc.lng.toString())
      }
      const to = {
        lat: parseFloat(vehicleLoc.lat.toString()),
        lng: parseFloat(vehicleLoc.lng.toString())
      }
      map.value.drawCustomDirection(from, to, {
        polylineStyle: {
          zIndex: 201,
          strokeColor: '#03b4f2',
          strokeOpacity: 1.0,
          strokeWeight: 12
        }
      })
    }
  })
}

const getMapInstance = () => {
  return map.value?.getMapInstance() || null
}

defineExpose({
  ready,
  createMarkers,
  removeMarkers,
  directionCreateMarkers,
  resetMap,
  clearDirections,
  reDrawRoute,
  getMapInstance,
  renderExpectedPaths,
  renderTrackingPaths
})
</script>
